/**
 * Fetches Shopify Bundles components and drives the selectable list +
 * Description / Specification / Reviews panels for the selected item.
 *
 * Uses a light first query for the included-items list, then loads heavy
 * metafields (specs/features) only for the selected component. This keeps
 * large 4–6+ appliance bundles within Storefront API limits.
 */
(() => {
  const root = document.querySelector('[data-bundle-components]');
  if (!root) return;

  const apiUrl = root.dataset.storefrontApiUrl;
  const token = root.dataset.storefrontApiToken;
  const handle = root.dataset.productHandle;
  const variantGid = root.dataset.variantGid || '';
  const bundleTitle = root.dataset.productTitle || '';
  const listRoots = Array.from(document.querySelectorAll('[data-bundle-included-list]'));
  const itemsByKey = new Map();
  const detailCache = new Map();
  let selectedKey = null;
  let resolveReady;
  let rejectReady;

  window.ParkAveBundle = {
    components: [],
    ready: new Promise((resolve, reject) => {
      resolveReady = resolve;
      rejectReady = reject;
    }),
    getAddToCartItems(quantity = 1) {
      return this.components
        .map((item) => {
          const variantId = parseInt(String(item.variantId || '').split('/').pop(), 10);
          if (!variantId) return null;

          const line = {
            id: variantId,
            quantity: Math.max(1, quantity) * (item.quantity || 1)
          };

          if (bundleTitle) {
            line.properties = {
              Bundle: bundleTitle
            };
          }

          return line;
        })
        .filter(Boolean);
    }
  };

  function getMessage(key, fallback) {
    const source = listRoots[0];
    return source?.dataset?.[key] || fallback;
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function setStatus(message) {
    listRoots.forEach((listRoot) => {
      listRoot.innerHTML = `<p class="bundle-included-items__status">${escapeHtml(message)}</p>`;
    });
  }

  function toNumericId(gidOrId) {
    if (!gidOrId) return '';
    const parts = String(gidOrId).split('/');
    return parts[parts.length - 1];
  }

  function metafieldValue(metafields, key) {
    if (!Array.isArray(metafields)) return '';
    const match = metafields.find((field) => field && field.key === key);
    return match?.value || '';
  }

  function parseJsonValue(raw) {
    if (raw == null || raw === '') return null;
    if (typeof raw === 'object') return raw;
    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  function pickNumber(...values) {
    for (const value of values) {
      if (value == null || value === '') continue;
      const number = typeof value === 'number' ? value : parseFloat(String(value).replace(/[^\d.-]/g, ''));
      if (Number.isFinite(number)) return number;
    }
    return null;
  }

  function parseReviewData(product) {
    const bvRating = parseJsonValue(product?.bvRating?.value);
    const bvRatingCount = pickNumber(product?.bvRatingCount?.value);

    const rating = pickNumber(bvRating?.value, bvRating?.rating);
    const count = pickNumber(bvRatingCount);
    const scaleMax = pickNumber(bvRating?.scale_max, bvRating?.scaleMax) || 5;

    if (rating == null && count == null) return null;

    return {
      rating: rating == null ? 0 : rating,
      count: count == null ? 0 : Math.round(count),
      scaleMax
    };
  }

  function renderStarsMarkup(rating, scaleMax = 5) {
    const value = Math.max(0, Number(rating) || 0);
    const max = Math.max(1, Number(scaleMax) || 5);
    let html = '';

    for (let i = 1; i <= max; i += 1) {
      // Exact fraction: 4.8 → star 5 filled at 80% (not rounded up to full).
      const fill = Math.max(0, Math.min(1, value - (i - 1)));
      const fillPct = Math.round(fill * 100);

      html += `<span class="bundle-star" aria-hidden="true"><span class="bundle-star__empty">★</span><span class="bundle-star__fill" style="width: ${fillPct}%">★</span></span>`;
    }

    return html;
  }

  function renderRatingHtml(item) {
    const review = item.review;
    const productId = escapeHtml(item.productNumericId || '');

    if (review && (review.rating > 0 || review.count > 0)) {
      const ratingValue = Number(review.rating) || 0;
      const rounded = ratingValue.toFixed(1).replace(/\.0$/, '');
      const label = escapeHtml(
        `${rounded} out of ${review.scaleMax}${review.count ? `, ${review.count} reviews` : ''}`
      );
      const valueHtml = ratingValue
        ? `<span class="bundle-included-item__rating-value">${escapeHtml(rounded)}</span>`
        : '';
      const countHtml = review.count
        ? `<span class="rating__count">(${escapeHtml(String(review.count))})</span>`
        : '';

      return `
        <span class="bundle-included-item__rating" role="img" aria-label="${label}">
          <span class="bundle-stars">${renderStarsMarkup(ratingValue, review.scaleMax)}</span>
          ${valueHtml}
          ${countHtml}
        </span>
      `;
    }

    if (!productId) return '';

    return `
      <span class="bundle-included-item__rating">
        <span
          data-bv-show="inline_rating"
          data-bv-product-id="${productId}"
          data-bv-seo="false"
        ></span>
      </span>
    `;
  }

  function reloadBazaarvoice() {
    if (window.$BV && typeof window.$BV.ui === 'function') {
      try {
        window.$BV.ui('rr', 'reload', true);
      } catch (error) {
        console.warn('[BundleComponents] Bazaarvoice reload failed:', error);
      }
    }
  }

  if (!apiUrl || !token || (!handle && !variantGid)) {
    console.warn('[BundleComponents] Missing Storefront API URL, token, or product handle');
    setStatus(getMessage('errorMessage', 'Unable to load included items.'));
    if (typeof rejectReady === 'function') {
      rejectReady(new Error('Missing Storefront API credentials'));
    }
    return;
  }

  const listQueryByVariant = `
    query BundleComponentsByVariant($id: ID!) {
      node(id: $id) {
        ... on ProductVariant {
          id
          sku
          title
          requiresComponents
          components(first: 30) {
            nodes {
              quantity
              productVariant {
                id
                sku
                title
                availableForSale
                image {
                  url
                  altText
                }
                product {
                  id
                  title
                  handle
                  vendor
                  featuredImage {
                    url
                    altText
                  }
                  bvRating: metafield(namespace: "custom", key: "bv_rating") {
                    type
                    value
                  }
                  bvRatingCount: metafield(namespace: "custom", key: "bv_rating_count") {
                    type
                    value
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  const listQueryByHandle = `
    query BundleComponentsByHandle($handle: String!) {
      product(handle: $handle) {
        id
        title
        handle
        variants(first: 1) {
          nodes {
            id
            sku
            title
            requiresComponents
            components(first: 30) {
              nodes {
                quantity
                productVariant {
                  id
                  sku
                  title
                  availableForSale
                  image {
                    url
                    altText
                  }
                  product {
                    id
                    title
                    handle
                    vendor
                    featuredImage {
                      url
                      altText
                    }
                    bvRating: metafield(namespace: "custom", key: "bv_rating") {
                      type
                      value
                    }
                    bvRatingCount: metafield(namespace: "custom", key: "bv_rating_count") {
                      type
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  const detailQuery = `
    query BundleComponentDetail($id: ID!) {
      node(id: $id) {
        ... on Product {
          id
          description
          descriptionHtml
          metafields(
            identifiers: [
              { namespace: "custom", key: "specs_html" }
              { namespace: "custom", key: "feature_html" }
              { namespace: "custom", key: "feature_bullet_points_long" }
              { namespace: "custom", key: "feature_overview_json" }
            ]
          ) {
            key
            value
          }
        }
      }
    }
  `;

  async function storefrontRequest(query, variables) {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({ query, variables })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const payload = await response.json();

    if (payload.errors?.length) {
      console.error('[BundleComponents] GraphQL errors:', payload.errors);
    }

    return payload;
  }

  async function fetchBundleComponents() {
    if (variantGid) {
      const byVariant = await storefrontRequest(listQueryByVariant, { id: variantGid });
      const variantNode = byVariant.data?.node;
      if (variantNode?.components) {
        return { variants: { nodes: [variantNode] } };
      }
    }

    if (!handle) return null;

    const byHandle = await storefrontRequest(listQueryByHandle, { handle });
    return byHandle.data?.product ?? null;
  }

  async function fetchComponentDetails(productGid) {
    if (!productGid) return null;
    if (detailCache.has(productGid)) return detailCache.get(productGid);

    const payload = await storefrontRequest(detailQuery, { id: productGid });
    const product = payload.data?.node ?? null;
    detailCache.set(productGid, product);
    return product;
  }

  function flattenComponents(productOrVariantWrapper) {
    if (!productOrVariantWrapper?.variants?.nodes) return [];

    const items = [];

    productOrVariantWrapper.variants.nodes.forEach((variant) => {
      const components = variant.components?.nodes || [];
      components.forEach((component) => {
        const componentVariant = component.productVariant;
        const componentProduct = componentVariant?.product;
        const key = componentVariant?.id || componentProduct?.id;
        if (!key || itemsByKey.has(key)) return;

        const item = {
          key,
          quantity: component.quantity,
          variantId: componentVariant?.id,
          sku: componentVariant?.sku,
          variantTitle: componentVariant?.title,
          productId: componentProduct?.id,
          productNumericId: toNumericId(componentProduct?.id),
          title: componentProduct?.title,
          handle: componentProduct?.handle,
          vendor: componentProduct?.vendor,
          review: parseReviewData(componentProduct),
          description: '',
          descriptionHtml: '',
          specsHtml: '',
          featureHtml: '',
          featureBullets: '',
          detailsLoaded: false,
          image:
            componentVariant?.image?.url ||
            componentProduct?.featuredImage?.url ||
            null,
          imageAlt:
            componentVariant?.image?.altText ||
            componentProduct?.featuredImage?.altText ||
            componentProduct?.title ||
            '',
          url: componentProduct?.handle ? `/products/${componentProduct.handle}` : null
        };

        itemsByKey.set(key, item);
        items.push(item);
      });
    });

    return items;
  }

  function renderItem(item, isSelected) {
    const quantity = item.quantity || 1;
    const title = escapeHtml(item.title || item.variantTitle || 'Product');
    const vendor = item.vendor
      ? `<span class="bundle-included-item__vendor">${escapeHtml(item.vendor)}</span>`
      : '';
    const modelLabel = escapeHtml(getMessage('modelLabel', 'Model'));
    const model = item.sku
      ? `<span class="bundle-included-item__model"><span class="bundle-included-item__model-label">${modelLabel}</span> <span class="bundle-included-item__model-value">${escapeHtml(item.sku)}</span></span>`
      : '';
    const rating = renderRatingHtml(item);
    const qtyLabel = getMessage('qtyLabel', 'Qty: __QTY__').replace('__QTY__', String(quantity));
    const image = item.image
      ? `<img class="bundle-included-item__image" src="${escapeHtml(item.image)}" alt="${escapeHtml(item.imageAlt)}" width="72" height="72" loading="lazy">`
      : `<span class="bundle-included-item__image bundle-included-item__image--placeholder" aria-hidden="true"></span>`;
    const selectedClass = isSelected ? ' is-selected' : '';
    const ariaSelected = isSelected ? 'true' : 'false';

    return `
      <li>
        <div
          class="bundle-included-item${selectedClass}"
          data-bundle-item
          data-item-key="${escapeHtml(item.key)}"
          data-product-id="${escapeHtml(item.productId || '')}"
          data-product-handle="${escapeHtml(item.handle || '')}"
          data-variant-id="${escapeHtml(item.variantId || '')}"
          data-product-url="${escapeHtml(item.url || '')}"
          role="button"
          tabindex="0"
          aria-pressed="${ariaSelected}"
          aria-selected="${ariaSelected}"
        >
          ${image}
          <span class="bundle-included-item__body">
            ${vendor}
            <span class="bundle-included-item__title">${title}</span>
            ${model}
            ${rating}
            <span class="bundle-included-item__qty">${escapeHtml(qtyLabel)}</span>
          </span>
        </div>
      </li>
    `;
  }

  function renderDescriptionHtml(item) {
    const parts = [];

    if (item.featureBullets) {
      const bullets = item.featureBullets
        .split('||')
        .map((bullet) => bullet.trim())
        .filter(Boolean);
      if (bullets.length) {
        parts.push(
          `<ul class="long-feature-bullet">${bullets
            .map((bullet) => `<li>${escapeHtml(bullet)}</li>`)
            .join('')}</ul>`
        );
      }
    }

    if (item.featureHtml) {
      parts.push(`<div class="inner_container">${item.featureHtml}</div>`);
    }

    if (item.descriptionHtml) {
      parts.push(`<div class="inner_container">${item.descriptionHtml}</div>`);
    } else if (item.description) {
      parts.push(`<div class="inner_container"><p>${escapeHtml(item.description)}</p></div>`);
    }

    if (!parts.length) {
      return `<p class="bundle-tab-panel__empty">${escapeHtml(
        getMessage('noDescription', 'No description is available for this item.')
      )}</p>`;
    }

    return parts.join('');
  }

  function renderSpecificationHtml(item) {
    if (item.specsHtml) {
      return item.specsHtml;
    }

    return `<p class="bundle-tab-panel__empty">${escapeHtml(
      getMessage('noSpecification', 'No specifications are available for this item.')
    )}</p>`;
  }

  function renderReviewsHtml(item) {
    const productId = escapeHtml(item.productNumericId || '');
    if (!productId) {
      return `<p class="bundle-tab-panel__empty">${escapeHtml(
        getMessage('noReviews', 'No reviews are available for this item.')
      )}</p>`;
    }

    return `<div data-bv-show="reviews" data-bv-product-id="${productId}"></div>`;
  }

  function updateTabPanels(item) {
    document.querySelectorAll('[data-bundle-tab-panel="description"]').forEach((panel) => {
      panel.innerHTML = renderDescriptionHtml(item);
    });

    document.querySelectorAll('[data-bundle-tab-panel="specification"]').forEach((panel) => {
      panel.innerHTML = renderSpecificationHtml(item);
    });

    document.querySelectorAll('[data-bundle-tab-panel="reviews"]').forEach((panel) => {
      panel.innerHTML = renderReviewsHtml(item);
    });

    if (window.$BV && typeof window.$BV.ui === 'function') {
      try {
        window.$BV.ui('rr', 'reload', true);
      } catch (error) {
        console.warn('[BundleComponents] Bazaarvoice reload failed:', error);
      }
    }
  }

  function renderList() {
    const items = Array.from(itemsByKey.values());
    const heading = escapeHtml(getMessage('listLabel', 'Included items:'));

    listRoots.forEach((listRoot) => {
      listRoot.innerHTML = `
        <h3 class="bundle-included-items__heading">${heading}</h3>
        <ul class="bundle-included-items__list" role="listbox" aria-label="${heading}">
          ${items.map((item) => renderItem(item, item.key === selectedKey)).join('')}
        </ul>
      `;
    });

    bindItemClicks();
    reloadBazaarvoice();
  }

  function updateSelectedState(key) {
    selectedKey = key;

    document.querySelectorAll('[data-bundle-item]').forEach((button) => {
      const isSelected = button.dataset.itemKey === key;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });
  }

  async function ensureItemDetails(item) {
    if (!item || item.detailsLoaded || !item.productId) return item;

    try {
      const detail = await fetchComponentDetails(item.productId);
      if (detail) {
        const metafields = detail.metafields || [];
        item.description = detail.description || '';
        item.descriptionHtml = detail.descriptionHtml || '';
        item.specsHtml = metafieldValue(metafields, 'specs_html');
        item.featureHtml = metafieldValue(metafields, 'feature_html');
        item.featureBullets = metafieldValue(metafields, 'feature_bullet_points_long');
      }
    } catch (error) {
      console.warn('[BundleComponents] Failed to load item details:', error);
    }

    item.detailsLoaded = true;
    return item;
  }

  async function selectItem(key, { force = false } = {}) {
    if (!itemsByKey.has(key)) return;
    if (!force && selectedKey === key) return;

    const item = itemsByKey.get(key);
    updateSelectedState(key);

    document.querySelectorAll('[data-bundle-tab-panel="description"], [data-bundle-tab-panel="specification"]').forEach((panel) => {
      panel.innerHTML = `<p class="bundle-included-items__status">${escapeHtml(
        getMessage('loadingMessage', 'Loading included items…')
      )}</p>`;
    });

    await ensureItemDetails(item);
    if (selectedKey !== key) return;

    updateTabPanels(item);

    document.dispatchEvent(
      new CustomEvent('bundle:item-select', {
        detail: {
          productId: item.productId,
          productHandle: item.handle,
          variantId: item.variantId,
          productUrl: item.url,
          item
        }
      })
    );
  }

  function bindItemClicks() {
    document.querySelectorAll('[data-bundle-item]').forEach((card) => {
      card.addEventListener('click', (event) => {
        if (event.target.closest('a, button, [data-bv-show]')) return;
        selectItem(card.dataset.itemKey);
      });

      card.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        selectItem(card.dataset.itemKey);
      });
    });
  }

  fetchBundleComponents()
    .then((product) => {
      if (!product) {
        console.warn('[BundleComponents] No product returned for handle:', handle, 'variant:', variantGid);
        setStatus(
          getMessage(
            'unpublishedMessage',
            'This bundle must be available on the Online Store sales channel to load included items.'
          )
        );
        if (typeof rejectReady === 'function') {
          rejectReady(new Error('Bundle product is not available via Storefront API'));
        }
        return;
      }

      const components = flattenComponents(product);

      if (!components.length) {
        setStatus(getMessage('emptyMessage', 'No included items found for this bundle.'));
        if (typeof rejectReady === 'function') {
          rejectReady(new Error('No bundle components found'));
        }
        return;
      }

      window.ParkAveBundle.components = components;
      selectedKey = components[0].key;
      renderList();
      selectItem(selectedKey, { force: true });

      if (typeof resolveReady === 'function') {
        resolveReady(components);
      }

      document.dispatchEvent(
        new CustomEvent('bundle:components-loaded', {
          detail: {
            product,
            components,
            selectedKey
          }
        })
      );
    })
    .catch((error) => {
      console.error('[BundleComponents] Failed to fetch components:', error);
      setStatus(getMessage('errorMessage', 'Unable to load included items.'));
      if (typeof rejectReady === 'function') {
        rejectReady(error);
      }
    });
})();
